import { useQuery } from '@tanstack/react-query'
import { Banner, ErrorBanner, Panel, Spinner } from '@shop/ui'

type Release = { version: string; file: string; size: number | null; date: string | null }

/**
 * يقرأ `latest.yml` — <b>الملفَّ نفسَه الذي يسأله البرنامجُ المثبَّت عن التحديث</b>.
 * فالزرُّ هنا يشير دائمًا إلى آخر نسخةٍ رُفعت، بلا تعديلِ هذه الشاشة في كلّ إصدار.
 */
async function latest(): Promise<Release> {
  const res = await fetch('/desktop/latest.yml', { cache: 'no-store' })

  if (!res.ok) {
    throw new Error('لا نسخةَ مرفوعةً من البرنامج بعد.')
  }

  const text = await res.text()
  const field = (name: string) => text.match(new RegExp(`^${name}:\\s*'?([^'\\n]+)'?`, 'm'))?.[1]?.trim() ?? null
  const size = text.match(/^\s+size:\s*(\d+)/m)?.[1]

  return {
    version: field('version') ?? '؟',
    file: field('path') ?? '',
    size: size ? Number(size) : null,
    date: field('releaseDate'),
  }
}

/**
 * برنامجُ ويندوز — <b>طلبُ المالك ٢٠٢٦-١٠-١٠</b>: «حط الملف اثبته من نفس لوحة التحكم».
 *
 * ⚠ **والتنزيلُ من الخادم نفسِه** (`/desktop/`) — لا رابطَ خارجيٌّ يُحفظ في
 *   رسالة ويضيع. ومن هنا يُنزَّل على كلّ جهازٍ جديد في المحلّ.
 */
export default function WindowsApp() {
  const release = useQuery({ queryKey: ['windows-app'], queryFn: latest, staleTime: 60_000 })

  return (
    <Panel title="برنامج ويندوز">
      <p className="text-sm text-ink-soft">
        نقطةُ البيع ولوحةُ الإدارة في برنامجٍ على الكمبيوتر — <b>ويبيع حتّى لو انقطع النت</b>،
        ويرحّل البيعَ لحاله لما يرجع.
      </p>

      <ErrorBanner error={release.error} />

      {release.isPending ? (
        <Spinner />
      ) : release.data && (
        <div className="mt-4 grid gap-4">
          <a
            href={`/desktop/${release.data.file}`}
            download
            className="inline-flex min-h-14 w-fit items-center gap-2 rounded-xl bg-brand px-6 text-lg font-bold text-white"
          >
            ⬇ نزّل البرنامج
          </a>

          <p className="text-xs text-ink-soft">
            النسخة <b dir="ltr">{release.data.version}</b>
            {release.data.size && <> · {Math.round(release.data.size / 1_048_576)} ميغا</>}
            {release.data.date && <> · {new Date(release.data.date).toLocaleDateString('ar-JO-u-nu-latn')}</>}
          </p>

          <ol className="grid list-decimal gap-2 ps-5 text-sm">
            <li><b>استنّى التنزيل يخلص كامل</b> قبل ما تفتح الملف.</li>
            <li>
              شغّل الملف. إذا طلع «Windows protected your PC»: اكبس <b dir="ltr">More info</b> ثمّ{' '}
              <b dir="ltr">Run anyway</b> — البرنامج مش موقّع بشهادة مدفوعة.
            </li>
            <li>كمّل التثبيت، بتطلع أيقونة «عال العال للموبايل» على سطح المكتب.</li>
            <li>
              <b>أوّل مرّة والنت شغّال:</b> فوت بحسابك وافتح الورديّة — هيك بيحفظ الأصناف والأسعار على الجهاز.
            </li>
          </ol>

          <Banner tone="info">
            بالبرنامج: <b dir="ltr">Ctrl+1</b> نقطة البيع · <b dir="ltr">Ctrl+2</b> لوحة الإدارة.
            والبرنامج بيحدّث حاله لحاله.
          </Banner>
        </div>
      )}
    </Panel>
  )
}
